import { useState } from 'react';
import { ArrowLeft, Plus, Edit2, Trash2, Clock, Activity, Target, AlertCircle, TrendingUp, Calendar } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Alert, AlertDescription } from '../ui/alert';
import { toast } from 'sonner';
import { PatientLayout } from './PatientLayout';
import { useAuth } from '../../lib/hooks/useAuth';
import {
  usePatientExerciseSessions,
  useLogExerciseSession,
  useUpdateExerciseSession,
  useDeleteExerciseSession,
  usePatientCustomExercisePlans,
} from '../../lib/hooks/useExercises';
import { LoadingState } from '../common/LoadingState';
import { EmptyState } from '../common/EmptyState';
import { AlertCard } from '../common/AlertCard';
import type { ExerciseSession } from '../../lib/api/exercises';

interface ExerciseLogManagementProps {
  onBack?: () => void;
  onNavigate?: (view: string) => void;
  onLogout?: () => void;
}

export function ExerciseLogManagement({ onBack, onNavigate, onLogout }: ExerciseLogManagementProps) {
  // ✅ PERMANENT FIX: No auth blocking - let API handle authentication
  const { user } = useAuth();
  const patientId = user?.patient_id;

  // ✅ DEBUG: Log user object to help diagnose issue
  console.log('🏋️ ExerciseLogManagement - User object:', {
    user,
    patient_id: user?.patient_id,
    role: user?.role,
    id: user?.id,
    email: user?.email
  });

  // ✅ FIX: Show error if patient_id is missing (not properly set up in database)
  const hasPatientId = patientId && patientId > 0;
  
  console.log('🏋️ ExerciseLogManagement - Patient ID check:', {
    patientId,
    hasPatientId,
    willShowError: !hasPatientId
  });

  // Fetch data - backend handles authentication via JWT
  const { data: sessionsData, isLoading, error, refetch } = usePatientExerciseSessions(patientId || 0, { days: 30 });
  const { data: customPlansData } = usePatientCustomExercisePlans(patientId || 0, true);

  // Mutations
  const logSession = useLogExerciseSession();
  const updateSession = useUpdateExerciseSession();
  const deleteSession = useDeleteExerciseSession();

  // State
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedSession, setSelectedSession] = useState<ExerciseSession | null>(null);
  const [formData, setFormData] = useState({
    exercise_plan_id: 0,
    duration_seconds: 0,
    completed_sets: 0,
    completed_reps: 0,
    difficulty_rating: 3,
    pain_level: 0,
    fatigue_level: 0,
    is_completed: true,
    patient_notes: '',
  });

  // Get exercise plans for the dropdown
  const customPlans = customPlansData?.exercise_plans || [];
  const availablePlans = customPlans.filter(plan => plan.is_active);

  // Filter sessions by last 7 days for edit/delete
  const sessions = sessionsData?.sessions || [];
  const now = new Date();
  const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
  
  const recentSessions = sessions.filter(session => {
    const sessionDate = new Date(session.session_date);
    return sessionDate >= sevenDaysAgo;
  });

  const canEdit = (session: ExerciseSession) => {
    const sessionDate = new Date(session.session_date);
    return sessionDate >= sevenDaysAgo;
  };

  // Handlers
  const handleCreate = () => {
    if (availablePlans.length === 0) {
      toast.error('No active exercise plan', {
        description: 'You need an active exercise plan from your therapist to log exercises.',
      });
      return;
    }

    setFormData({
      exercise_plan_id: availablePlans[0]?.id || 0,
      duration_seconds: 600, // 10 minutes default
      completed_sets: 1,
      completed_reps: 10,
      difficulty_rating: 3,
      pain_level: 0,
      fatigue_level: 0,
      is_completed: true,
      patient_notes: '',
    });
    setIsCreateDialogOpen(true);
  };

  const handleEdit = (session: ExerciseSession) => {
    if (!canEdit(session)) {
      toast.error('Cannot edit this session', {
        description: 'You can only edit exercise sessions from the last 7 days.',
      });
      return;
    }

    setSelectedSession(session);
    setFormData({
      exercise_plan_id: session.exercise_plan_id,
      duration_seconds: session.duration_seconds || 0,
      completed_sets: session.completed_sets || 0,
      completed_reps: session.completed_reps || 0,
      difficulty_rating: session.difficulty_rating || 3,
      pain_level: session.pain_level || 0,
      fatigue_level: session.fatigue_level || 0,
      is_completed: session.is_completed,
      patient_notes: session.patient_notes || '',
    });
    setIsEditDialogOpen(true);
  };

  const handleDelete = (session: ExerciseSession) => {
    if (!canEdit(session)) {
      toast.error('Cannot delete this session', {
        description: 'You can only delete exercise sessions from the last 7 days.',
      });
      return;
    }

    setSelectedSession(session);
    setIsDeleteDialogOpen(true);
  };

  const handleSubmitCreate = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!hasPatientId) {
      toast.error('Patient information not available', {
        description: 'Please contact your care coordinator to complete your profile setup.',
      });
      return;
    }

    if (formData.exercise_plan_id === 0) {
      toast.error('Please select an exercise plan');
      return;
    }

    try {
      await logSession.mutateAsync({
        ...formData,
        patient_id: patientId!,
      });
      setIsCreateDialogOpen(false);
      refetch();
    } catch (error: any) {
      // Error handled by mutation
    }
  };

  const handleSubmitEdit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!hasPatientId) {
      toast.error('Patient information not available', {
        description: 'Please contact your care coordinator to complete your profile setup.',
      });
      return;
    }

    if (!selectedSession) return;

    try {
      await updateSession.mutateAsync({
        sessionId: selectedSession.id,
        data: {
          ...formData,
          patient_id: patientId!,
        },
      });
      setIsEditDialogOpen(false);
      setSelectedSession(null);
      refetch();
    } catch (error: any) {
      // Error handled by mutation
    }
  };

  const handleConfirmDelete = async () => {
    if (!selectedSession) return;

    try {
      await deleteSession.mutateAsync(selectedSession.id);
      setIsDeleteDialogOpen(false);
      setSelectedSession(null);
      refetch();
    } catch (error: any) {
      // Error handled by mutation
    }
  };

  const formatDuration = (seconds: number | null) => {
    if (!seconds) return '0 min';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}m ${secs}s`;
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
      return `Today, ${date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}`;
    } else if (date.toDateString() === yesterday.toDateString()) {
      return `Yesterday, ${date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}`;
    } else {
      return date.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    }
  };

  const getDifficultyColor = (rating: number | null) => {
    if (!rating) return 'bg-gray-500';
    if (rating <= 2) return 'bg-[#14CAF4]';
    if (rating <= 3) return 'bg-[#F07D13]';
    return 'bg-destructive';
  };

  // ✅ FIX: Check if patient_id is missing - this means patient profile not set up
  if (!hasPatientId) {
    return (
      <PatientLayout currentView="exercise-log" onNavigate={onNavigate} onLogout={onLogout}>
        <div className="p-4">
          <AlertCard
            type="error"
            title="Patient information not available"
            description="Your patient profile is not properly set up. Please contact your care coordinator or system administrator to complete your profile setup."
            action={
              <Button onClick={() => onLogout?.()} variant="outline">
                Logout and Try Again
              </Button>
            }
          />
        </div>
      </PatientLayout>
    );
  }

  // Loading state
  if (isLoading) {
    return (
      <PatientLayout currentView="exercise-log" onNavigate={onNavigate} onLogout={onLogout}>
        <LoadingState message="Loading exercise logs..." />
      </PatientLayout>
    );
  }

  // Error state
  if (error) {
    return (
      <PatientLayout currentView="exercise-log" onNavigate={onNavigate} onLogout={onLogout}>
        <div className="p-4">
          <AlertCard
            type="error"
            title="Failed to load exercise logs"
            description={error.message || 'There was an error loading your exercise logs.'}
            action={
              <Button onClick={() => refetch()} variant="outline">
                Try Again
              </Button>
            }
          />
        </div>
      </PatientLayout>
    );
  }

  return (
    <PatientLayout currentView="exercise-log" onNavigate={onNavigate} onLogout={onLogout}>
      {/* Header */}
      <div className="bg-primary p-6 rounded-b-3xl shadow-md">
        <div className="flex items-center gap-4">
          {onBack && (
            <button onClick={onBack} className="text-primary-foreground">
              <ArrowLeft className="w-6 h-6" />
            </button>
          )}
          <div className="flex-1">
            <h1 className="text-primary-foreground">Exercise Log</h1>
            <p className="text-primary-foreground/80">Track your exercise sessions</p>
          </div>
          <Button
            onClick={handleCreate}
            className="bg-accent hover:bg-accent/90 text-white"
            size="sm"
          >
            <Plus className="w-4 h-4 mr-1" />
            Log Exercise
          </Button>
        </div>
      </div>

      <div className="p-4 space-y-4">
        {/* Info Alert */}
        {availablePlans.length === 0 && (
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              You don't have any active exercise plans yet. Your therapist will create a personalized plan for you.
            </AlertDescription>
          </Alert>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-3 gap-3">
          <Card>
            <CardContent className="p-4 text-center">
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-2">
                <Activity className="w-5 h-5 text-primary" />
              </div>
              <p className="text-primary text-xl">{sessions.length}</p>
              <p className="text-muted-foreground text-xs">Total</p>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4 text-center">
              <div className="w-10 h-10 rounded-full bg-[#14CAF4]/10 flex items-center justify-center mx-auto mb-2">
                <TrendingUp className="w-5 h-5 text-[#14CAF4]" />
              </div>
              <p className="text-[#14CAF4] text-xl">{recentSessions.length}</p>
              <p className="text-muted-foreground text-xs">Last 7 days</p>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4 text-center">
              <div className="w-10 h-10 rounded-full bg-accent/10 flex items-center justify-center mx-auto mb-2">
                <Target className="w-5 h-5 text-accent" />
              </div>
              <p className="text-accent text-xl">
                {sessions.filter(s => s.is_completed).length}
              </p>
              <p className="text-muted-foreground text-xs">Completed</p>
            </CardContent>
          </Card>
        </div>

        {/* Edit/Delete Note */}
        {recentSessions.length > 0 && (
          <Alert className="bg-[#14CAF4]/10 border-[#14CAF4]">
            <AlertCircle className="h-4 w-4 text-[#14CAF4]" />
            <AlertDescription className="text-[#14CAF4]">
              You can edit or delete exercise sessions from the last 7 days only.
            </AlertDescription>
          </Alert>
        )}

        {/* Exercise Sessions List */}
        <div className="space-y-3">
          <h3 className="text-primary">Exercise History</h3>

          {sessions.length === 0 ? (
            <EmptyState
              icon={Activity}
              title="No exercise logs yet"
              description="Start logging your exercise sessions to track your progress."
              action={
                availablePlans.length > 0 ? (
                  <Button onClick={handleCreate} className="bg-accent hover:bg-accent/90">
                    <Plus className="w-4 h-4 mr-2" />
                    Log First Exercise
                  </Button>
                ) : undefined
              }
            />
          ) : (
            sessions.map((session) => {
              const isEditable = canEdit(session);

              return (
                <Card key={session.id} className="shadow-sm">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h4 className="text-foreground">{session.exercise_name || 'Exercise Session'}</h4>
                          {session.is_completed && (
                            <Badge variant="outline" className="bg-[#14CAF4]/10 text-[#14CAF4] border-[#14CAF4] text-xs">
                              Completed
                            </Badge>
                          )}
                        </div>
                        <p className="text-muted-foreground text-sm">{session.exercise_category || 'General'}</p>
                        <div className="flex items-center gap-2 text-muted-foreground text-xs mt-1">
                          <Calendar className="w-3 h-3" />
                          <span>{formatDate(session.session_date)}</span>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        {isEditable ? (
                          <>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEdit(session)}
                            >
                              <Edit2 className="w-4 h-4 text-primary" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDelete(session)}
                            >
                              <Trash2 className="w-4 h-4 text-destructive" />
                            </Button>
                          </>
                        ) : (
                          <Badge variant="outline" className="text-xs text-muted-foreground">
                            Read-only
                          </Badge>
                        )}
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4 text-primary" />
                        <div>
                          <p className="text-muted-foreground text-xs">Duration</p>
                          <p className="text-foreground">{formatDuration(session.duration_seconds)}</p>
                        </div>
                      </div>

                      {session.completed_sets && session.completed_reps && (
                        <div className="flex items-center gap-2">
                          <Activity className="w-4 h-4 text-accent" />
                          <div>
                            <p className="text-muted-foreground text-xs">Sets × Reps</p>
                            <p className="text-foreground">
                              {session.completed_sets} × {session.completed_reps}
                            </p>
                          </div>
                        </div>
                      )}

                      {session.difficulty_rating && (
                        <div className="flex items-center gap-2">
                          <Target className="w-4 h-4 text-[#14CAF4]" />
                          <div>
                            <p className="text-muted-foreground text-xs">Difficulty</p>
                            <div className="flex items-center gap-1">
                              {[1, 2, 3, 4, 5].map((level) => (
                                <div
                                  key={level}
                                  className={`w-2 h-2 rounded-full ${
                                    level <= (session.difficulty_rating || 0)
                                      ? getDifficultyColor(session.difficulty_rating)
                                      : 'bg-gray-200'
                                  }`}
                                />
                              ))}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    {session.patient_notes && (
                      <div className="mt-3 pt-3 border-t">
                        <p className="text-muted-foreground text-xs mb-1">Notes</p>
                        <p className="text-foreground text-sm">{session.patient_notes}</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })
          )}
        </div>
      </div>

      {/* Create Dialog */}
      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Log Exercise Session</DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmitCreate} className="space-y-4">
            <div>
              <Label>Exercise Plan</Label>
              <select
                value={formData.exercise_plan_id}
                onChange={(e) => setFormData({ ...formData, exercise_plan_id: parseInt(e.target.value) })}
                className="w-full p-2 border rounded-md"
                required
              >
                {availablePlans.map((plan) => (
                  <option key={plan.id} value={plan.id}>
                    {plan.plan_title}
                  </option>
                ))}
              </select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Duration (minutes)</Label>
                <Input
                  type="number"
                  min="1"
                  value={Math.floor(formData.duration_seconds / 60)}
                  onChange={(e) =>
                    setFormData({ ...formData, duration_seconds: parseInt(e.target.value) * 60 })
                  }
                  required
                />
              </div>

              <div>
                <Label>Difficulty (1-5)</Label>
                <Input
                  type="number"
                  min="1"
                  max="5"
                  value={formData.difficulty_rating}
                  onChange={(e) => setFormData({ ...formData, difficulty_rating: parseInt(e.target.value) })}
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Sets</Label>
                <Input
                  type="number"
                  min="0"
                  value={formData.completed_sets}
                  onChange={(e) => setFormData({ ...formData, completed_sets: parseInt(e.target.value) })}
                />
              </div>

              <div>
                <Label>Reps</Label>
                <Input
                  type="number"
                  min="0"
                  value={formData.completed_reps}
                  onChange={(e) => setFormData({ ...formData, completed_reps: parseInt(e.target.value) })}
                />
              </div>
            </div>

            <div>
              <Label>Notes (optional)</Label>
              <Input
                value={formData.patient_notes}
                onChange={(e) => setFormData({ ...formData, patient_notes: e.target.value })}
                placeholder="How did you feel during this exercise?"
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" className="bg-accent hover:bg-accent/90" disabled={logSession.isPending}>
                {logSession.isPending ? 'Logging...' : 'Log Exercise'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Exercise Session</DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmitEdit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Duration (minutes)</Label>
                <Input
                  type="number"
                  min="1"
                  value={Math.floor(formData.duration_seconds / 60)}
                  onChange={(e) =>
                    setFormData({ ...formData, duration_seconds: parseInt(e.target.value) * 60 })
                  }
                  required
                />
              </div>

              <div>
                <Label>Difficulty (1-5)</Label>
                <Input
                  type="number"
                  min="1"
                  max="5"
                  value={formData.difficulty_rating}
                  onChange={(e) => setFormData({ ...formData, difficulty_rating: parseInt(e.target.value) })}
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Sets</Label>
                <Input
                  type="number"
                  min="0"
                  value={formData.completed_sets}
                  onChange={(e) => setFormData({ ...formData, completed_sets: parseInt(e.target.value) })}
                />
              </div>

              <div>
                <Label>Reps</Label>
                <Input
                  type="number"
                  min="0"
                  value={formData.completed_reps}
                  onChange={(e) => setFormData({ ...formData, completed_reps: parseInt(e.target.value) })}
                />
              </div>
            </div>

            <div>
              <Label>Notes (optional)</Label>
              <Input
                value={formData.patient_notes}
                onChange={(e) => setFormData({ ...formData, patient_notes: e.target.value })}
                placeholder="How did you feel during this exercise?"
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsEditDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" className="bg-accent hover:bg-accent/90" disabled={updateSession.isPending}>
                {updateSession.isPending ? 'Updating...' : 'Update Exercise'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Exercise Session</DialogTitle>
          </DialogHeader>

          <div className="py-4">
            <p className="text-foreground">
              Are you sure you want to delete this exercise session? This action cannot be undone.
            </p>
            {selectedSession && (
              <div className="mt-4 p-3 bg-secondary rounded-lg">
                <p className="text-sm text-foreground">
                  <strong>{selectedSession.exercise_name || 'Exercise Session'}</strong>
                </p>
                <p className="text-xs text-muted-foreground">{formatDate(selectedSession.session_date)}</p>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleConfirmDelete}
              disabled={deleteSession.isPending}
            >
              {deleteSession.isPending ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PatientLayout>
  );
}
